import React, { useMemo, useState } from 'react';
import styles from './index.module.scss';
import HeadContainer from '../../components/lm-d-s/head-container';
import WarningFooter from '../../components/lm-d-s/warning-footer/WarningFooter';
import CharacteristicScreen from '../../components/lm-d-s/characteristic-screen';
import { brandList } from './const';
import classNames from 'classnames';
import NoLongerManufacturedComponent from './components/no-longer-manufactured-component';

const BrandLineScreen = ({
  setScreenNumber,
  backButtonClick,
  nextButtonClick,
  brandId,
  withoutHeader = false,
  historyState = {},
  updateHistoryState = null
}) => {
  // History-driven when the parent wires up `updateHistoryState`,
  // otherwise falls back to plain internal state (default for most pages).
  const isHistoryEnabled = !!updateHistoryState;

  const brand = useMemo(
    () => brandList.find((item) => item.brandId === brandId),
    [brandId]
  );
  const [internalSelectedPack, setInternalSelectedPack] = useState(null);

  const selectedPack = useMemo(() => {
    if (isHistoryEnabled && historyState && historyState.selectedPackTitle) {
      return brand?.lines
        .flatMap((l) => l.packsLine)
        .find((p) => p.title === historyState.selectedPackTitle);
    }
    return internalSelectedPack;
  }, [isHistoryEnabled, historyState, internalSelectedPack, brand]);

  const handleSetSelectedPack = (pack) => {
    if (isHistoryEnabled) {
      if (pack) {
        updateHistoryState({ selectedPackTitle: pack.title });
      } else if (historyState && historyState.selectedPackTitle) {
        window.history.back();
      } else {
        setInternalSelectedPack(null);
      }
    } else {
      setInternalSelectedPack(pack);
    }
  };

  return brand && !selectedPack ? (
    <div
      className={styles.wrapperContainer}
      style={{ backgroundImage: brand.background }}
    >
      <div className={styles.container}>
        {!withoutHeader && <HeadContainer setScreenNumber={setScreenNumber} />}
        <div className={styles.contentContainer}>
          <p className={styles.title}>{brand.brandTitle}</p>
          <NoLongerManufacturedComponent brandId={brandId} />
          <div className={styles.linesContainer}>
            {brand.lines.map((line, index) => {
              return (
                <div key={index?.toString()} className={styles.lineItem}>
                  <p className={styles.lineTitle}>{line.lineTitle}</p>
                  <div className={styles.packList}>
                    {line.packsLine.map((packItem) => {
                      return (
                        <div
                          key={packItem.title}
                          className={classNames(
                            styles.packItem,
                            packItem.isDarkButton && styles.darkPackItem
                          )}
                          onClick={() => handleSetSelectedPack(packItem)}
                        >
                          <div className={styles.imageContainer}>
                            <img src={packItem.pack} alt={packItem.pack} />
                          </div>
                          <p>{packItem.title}</p>
                          <img
                            className={styles.arrow}
                            src="/images/all-brands/right-arrow.svg"
                            alt="arrow-right"
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {!!brand?.miniPack && brand?.miniPack?.length > 0 && (
              <div className={styles.miniLineItem}>
                <p className={styles.lineTitle}>Міні-блок</p>
                {brand?.miniPack?.map((miniPackItem, index) => {
                  return (
                    <div
                      key={index?.toString()}
                      className={styles.miniPackItem}
                    >
                      <div className={styles.textContainer}>
                        <div className={styles.titleContainer}>
                          <p>{miniPackItem?.title}</p>
                          <p style={{ fontWeight: 400 }}>
                            {miniPackItem?.packCount}
                          </p>
                        </div>
                        <div className={styles.linkContainer}>
                          {miniPackItem?.buttonList?.map((buttonItem) => {
                            return (
                              <a
                                href={buttonItem.link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {buttonItem.title}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                      <div className={styles.imageContainer}>
                        <img
                          src={miniPackItem.image}
                          alt={miniPackItem.title}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {!!brand?.bigPack && brand?.bigPack?.length > 0 && (
              <div className={styles.miniLineItem}>
                <p className={styles.lineTitle}>Збільшений блок</p>
                {brand?.bigPack?.map((bigPackItem, index) => {
                  return (
                    <div
                      key={index?.toString()}
                      className={styles.miniPackItem}
                    >
                      <div className={styles.textContainer}>
                        <div className={styles.titleContainer}>
                          <p>{bigPackItem?.title}</p>
                          <p style={{ fontWeight: 400 }}>
                            {bigPackItem?.packCount}
                          </p>
                        </div>
                        <div className={styles.linkContainer}>
                          {bigPackItem?.buttonList?.map((buttonItem) => {
                            return (
                              <a
                                href={buttonItem.link}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {buttonItem.title}
                              </a>
                            );
                          })}
                        </div>
                      </div>
                      <div className={styles.imageContainer}>
                        <img src={bigPackItem.image} alt={bigPackItem.title} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <div className={styles.buttonContainer}>
          <button className={styles.menuButton} onClick={backButtonClick}>
            Повернутися до вибору бренду
          </button>
          <button
            className={styles.reviewButton}
            style={{ marginTop: 10 }}
            onClick={nextButtonClick}
          >
            Далі
          </button>
        </div>
      </div>
      <WarningFooter />
    </div>
  ) : selectedPack && brand && !selectedPack?.isExternalPage ? (
    <CharacteristicScreen
      brandId={brand.brandId}
      isNewMixture={selectedPack.isNewMixture}
      newMixtureText={selectedPack?.newMixtureText ?? undefined}
      title={selectedPack.title}
      setScreenNumber={setScreenNumber}
      filterCharacteristicList={selectedPack.filterCharacteristicList}
      infoList={selectedPack.characteristicInfoList}
      tobaccoFirst={selectedPack.tobaccoFirst}
      tobaccoIcon={selectedPack?.tobaccoIcon}
      paperIcon={selectedPack?.paperIcon}
      paperText={selectedPack?.paperText}
      characteristicListTitle={selectedPack?.characteristicListTitle}
      tobaccoSecond={selectedPack.tobaccoSecond}
      packImage={selectedPack.pack}
      handleBackButton={() => handleSetSelectedPack(null)}
      handleNextButton={nextButtonClick}
      backgroundImage={selectedPack.background ?? brand.background}
      filterIcon={selectedPack.filterIcon}
      isMarlboroSpecial={selectedPack.isMarlboroSpecial}
      nextButtonTitle="Далі"
      fullRangeButtonClick={() => {
        backButtonClick();
        handleSetSelectedPack(null);
      }}
      style={selectedPack?.style}
    />
  ) : (
    selectedPack?.externalPage(() => handleSetSelectedPack(null))
  );
};

export default BrandLineScreen;
